// MCB Document Automation Agent — design + behavioral constants.
//
// Pure data only (no React, no JSX) so this file is fast to import
// from anywhere — components, tests, scripts — without pulling React
// into the dependency graph.
//
// What lives here:
//   - Brand palette `C` (navy, gold, warm whites, status colors)
//   - Role hierarchy (mirrors app/models/database.py)
//   - Undo window duration
//   - Document type labels for the extraction pipeline

// ============================================================
// Brand palette
// ============================================================
export const C = {
  navy: "#1B2A4A", navyLight: "#243660", navyDark: "#111D35",
  gold: "#C6993E", goldLight: "#D4AD5C", goldDim: "#8B6D2E",
  cream: "#FAF8F4", warmWhite: "#F5F2ED", warmGray: "#E8E4DD",
  textDark: "#1B2A4A", textMuted: "#6B7280", textLight: "#9CA3AF",
  success: "#166534", successBg: "#DCFCE7",
  error: "#991B1B", errorBg: "#FEE2E2",
  infoBorder: "#3B82F6",
};

// ============================================================
// Undo window
// ============================================================
// How long an "Undo upload" / "Undo push" affordance stays clickable
// after a successful Clio mutation. 30s is the conscious-correction
// window — the firm reported wanting "just enough time to catch a
// misclick or accidentally syncing the wrong info." Bumping later is
// a one-line change if real-world use shows it's too short.
export const UNDO_WINDOW_SECONDS = 30;

// ============================================================
// Role hierarchy (mirrors app/models/database.py)
// ============================================================
// Keep these in sync with the backend ROLE_HIERARCHY list. Low → High.
// Substep 4-λ (2026-08-13): expanded from 5 tiers to 7. Added
// support_staff (below paralegal), renamed attorney -> associate,
// renamed superadmin -> firm_admin, added platform_operator (Charpen
// cross-firm). Aliases below keep legacy strings working so existing
// user records + legacy backend calls don't break.
export const ROLE_HIERARCHY = [
  "pending",              // 0
  "support_staff",        // 1
  "paralegal",            // 2
  "associate",            // 3
  "partner",              // 4
  "firm_admin",           // 5
  "platform_operator",    // 6
];

// Legacy role strings → new-tier equivalents. Matches the
// _ROLE_ALIASES table in app/models/database.py.
const ROLE_ALIASES = {
  superadmin: "firm_admin",
  attorney:   "associate",
};

export function roleRank(role) {
  const normalized = (role || "").toLowerCase();
  const resolved = ROLE_ALIASES[normalized] || normalized;
  const i = ROLE_HIERARCHY.indexOf(resolved);
  return i < 0 ? -1 : i;
}

export function roleAtLeast(actual, required) {
  return roleRank(actual) >= roleRank(required);
}

export const ROLE_LABELS = {
  pending:            "Pending",
  support_staff:      "Support Staff",
  paralegal:          "Paralegal",
  associate:          "Associate",
  attorney:           "Associate",      // legacy alias -> display same as associate
  partner:            "Partner",
  firm_admin:         "Firm Admin",
  superadmin:         "Firm Admin",     // legacy alias -> display same as firm_admin
  platform_operator:  "Platform Operator",
};

export const ROLE_BADGE_COLORS = {
  pending:            { bg: "#94A3B820", fg: "#475569" },
  support_staff:      { bg: "#CBD5E120", fg: "#334155" },
  paralegal:          { bg: "#22C55E20", fg: "#15803D" },
  associate:          { bg: "#3B82F620", fg: "#1D4ED8" },
  attorney:           { bg: "#3B82F620", fg: "#1D4ED8" },  // alias
  partner:            { bg: "#A855F720", fg: "#7E22CE" },
  firm_admin:         { bg: "#F59E0B20", fg: "#B45309" },
  superadmin:         { bg: "#F59E0B20", fg: "#B45309" },  // alias
  platform_operator:  { bg: "#E11D4820", fg: "#9F1239" },  // Charpen red, distinct from firm_admin gold
};

// ============================================================
// Document type labels (extraction pipeline)
// ============================================================
export const DOC_TYPE_LABELS = {
  DL_FRONT: "DL Front",
  DL_BACK: "DL Back",
  INSURANCE_FRONT: "Insurance Front",
  INSURANCE_BACK: "Insurance Back",
  INTAKE_FORM: "Intake Form",
  CRASH_PHOTO: "Crash Photo",
  UNKNOWN: "Unknown",
};
